import type { HTMLAttributes } from 'react';
import { useMemo, useState } from 'react';

import { Badge } from '../components/badge';
import { Button } from '../components/button';
import { MediaPreviewDialog } from './media-preview-dialog';
import { cn } from '../lib/cn';

/**
 * One resolvable media entry for a GTIN. Presentational only: every route is a
 * ready-to-use same-origin BFF route (never a mediaserver/CDN URL with a key).
 * Images render via `thumbnailRoute`/`imageRoute`; documents (e.g. SDS PDF) render
 * as a download link — the gallery never previews a PDF.
 */
export interface MediaGalleryItem {
  mediaId: string;
  /** Raw mediaserver typeInfo, e.g. PRODUCT_IMAGE, SAFETY_DATA_SHEET. Used for grouping. */
  typeInfo: string;
  kind: 'image' | 'document';
  fileName: string;
  /** BFF redirect route for the small preview (image kind). */
  thumbnailRoute?: string;
  /** BFF redirect route for the enlarged web variant (image kind, click-to-enlarge). */
  imageRoute?: string;
  /** BFF redirect route for downloading the original bytes (document kind, or image download). */
  downloadRoute?: string;
  mimeType?: string;
  size?: number;
  /** Pixel width, when known (image kind only). Used by the metadata line (h2-4 fidelity). */
  width?: number;
  /** Pixel height, when known (image kind only). Used by the metadata line (h2-4 fidelity). */
  height?: number;
  /**
   * Optional coarse display bucket (e.g. the DAM's packshot/etiket/sds/marketing/overig).
   * Drives the type badge and, in `layout="tabs"`, the filter tabs. Falls back to `typeInfo`
   * when absent, so callers that never set this (h2-3) are unaffected.
   */
  displayType?: string;
}

export interface MediaGalleryLabels {
  /** Translate a raw typeInfo into a human group heading. */
  groupLabel: (typeInfo: string) => string;
  /** Informative empty state when the GTIN has no discoverable media. */
  emptyState: string;
  imageAlt: (fileName: string) => string;
  enlarge: (fileName: string) => string;
  download: (fileName: string) => string;
  dialogTitle: (fileName: string) => string;
  close: string;
  /** Accessible label for the neutral placeholder shown on image load error. */
  placeholder: string;
  /** Accessible label for the per-item selection toggle (when selectable). */
  select?: (fileName: string, selected: boolean) => string;
  /**
   * Short badge text for an item's bucket (`displayType` or `typeInfo`), e.g. "Packshot".
   * Optional — when omitted, no type badge is rendered (h2-3, unchanged).
   */
  typeBadge?: (bucket: string) => string;
  /**
   * One-line file metadata below the file name (e.g. "2400×2400 · PNG · 2,1 MB"), or `null`
   * when there is nothing to show for that item. Optional — when omitted, no metadata line
   * is rendered (h2-3, unchanged).
   */
  metaLine?: (item: MediaGalleryItem) => string | null;
  /**
   * Filter-tab label including its count, e.g. "Alle (9)" / "Packshot (3)". `bucket === null`
   * is the "all" tab. Required only when `layout="tabs"`.
   */
  tabLabel?: (bucket: string | null, count: number) => string;
  /** Accessible group label for the filter-tab row. Required only when `layout="tabs"`. */
  tabGroupLabel?: string;
}

/** Optional multi-select support, kept generic so the DAM (E11) can reuse it. */
export interface MediaGallerySelection {
  selectedIds: ReadonlySet<string>;
  onToggle: (mediaId: string) => void;
}

export interface MediaGalleryProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  items: MediaGalleryItem[];
  labels: MediaGalleryLabels;
  /**
   * typeInfo ordering priority (first = shown first). Types not listed sort after,
   * in first-seen order. Default puts product packshots first, then safety sheets.
   */
  typeOrder?: string[];
  /** When provided, renders a per-item selection checkbox and reflects selectedIds. */
  selection?: MediaGallerySelection;
  /**
   * When provided, every item renders a Download button that calls this instead of a
   * native link — required where delivery needs an authenticated fetch (bearer token).
   */
  onDownload?: (item: MediaGalleryItem) => void;
  /**
   * `'grouped'` (default, h2-3): vertical sections per type, unchanged. `'tabs'` (h2-4
   * mockup product-media.html): one flat grid with a type-filter tab bar above it, bucketed
   * by `displayType` (falls back to `typeInfo`). Requires `labels.tabLabel`.
   */
  layout?: 'grouped' | 'tabs';
}

const DEFAULT_TYPE_ORDER = ['PRODUCT_IMAGE', 'SAFETY_DATA_SHEET'];

function PlaceholderBox({ label }: { label: string }) {
  return (
    <div
      aria-label={label}
      className="flex h-32 w-full items-center justify-center rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background-muted)] text-[var(--foreground-muted)]"
      role="img"
    >
      <svg aria-hidden="true" className="h-6 w-6" fill="none" viewBox="0 0 24 24">
        <rect height="16" rx="2" stroke="currentColor" strokeWidth="1.5" width="18" x="3" y="4" />
        <circle cx="8.5" cy="9" fill="currentColor" r="1.5" />
        <path d="m4 18 5-5 3 3 3-3 5 5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      </svg>
    </div>
  );
}

function DocumentIcon() {
  return (
    <svg aria-hidden="true" className="h-6 w-6 shrink-0" fill="none" viewBox="0 0 24 24">
      <path d="M14 3v5h5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
      <path d="M6 3h8l5 5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24">
      <path d="M12 4v10m0 0 4-4m-4 4-4-4M5 19h14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
    </svg>
  );
}

/**
 * When `onDownload` is provided the caller needs an authenticated (Bearer) blob fetch
 * instead of a plain link — the per-file download route is same-origin but
 * Bearer-protected, so a native `<a href>` cannot reach it (h2-4). Without `onDownload`
 * (h2-3's internal gallery) a plain download link is used, unchanged.
 */
function DownloadButton({ item, labels, onDownload }: { item: MediaGalleryItem; labels: MediaGalleryLabels; onDownload: (item: MediaGalleryItem) => void }) {
  return (
    <button
      aria-label={labels.download(item.fileName)}
      className="mt-auto inline-flex items-center justify-center gap-1 rounded-[var(--radius-control)] border border-[var(--border)] px-3 py-1.5 text-sm font-medium text-[var(--secondary)] hover:bg-[var(--background-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
      onClick={() => onDownload(item)}
      type="button"
    >
      <DownloadIcon />
      {labels.download(item.fileName)}
    </button>
  );
}

/**
 * File name + one-line metadata (dimensions/format/size), shown only when the caller
 * supplies `labels.metaLine` (h2-4 mockup product-media.html `.m-meta`). Omitted entirely
 * for h2-3's internal gallery, which never supplies it — unchanged rendering there.
 */
function MediaMeta({ item, labels }: { item: MediaGalleryItem; labels: MediaGalleryLabels }) {
  if (labels.metaLine == null) return null;
  const metaText = labels.metaLine(item);
  return (
    <div className="min-w-0">
      <p className="truncate text-sm font-medium text-[var(--foreground)]" title={item.fileName}>{item.fileName}</p>
      {metaText != null && <p className="truncate text-xs text-[var(--foreground-muted)]">{metaText}</p>}
    </div>
  );
}

function GalleryImage({ item, labels, onDownload }: { item: MediaGalleryItem; labels: MediaGalleryLabels; onDownload?: (item: MediaGalleryItem) => void }) {
  const [thumbFailed, setThumbFailed] = useState(false);
  const thumbSrc = item.thumbnailRoute ?? item.imageRoute;
  const enlargedSrc = item.imageRoute ?? item.thumbnailRoute;
  const alt = labels.imageAlt(item.fileName);

  const thumb = !thumbFailed && thumbSrc != null
    ? (
      <img
        alt={alt}
        className="h-32 w-full rounded-[var(--radius-control)] border border-[var(--border)] object-contain"
        onError={() => setThumbFailed(true)}
        src={thumbSrc}
      />
    )
    : <PlaceholderBox label={labels.placeholder} />;

  return (
    <div className="flex flex-col gap-2">
      {/* Hetzelfde venster als de mediabibliotheek gebruikt. Er hoort er één van te bestaan: twee
          zouden uit elkaar gaan lopen. De galerij kent zijn adres al, dus hij levert het meteen. */}
      <MediaPreviewDialog
        labels={{
          title: labels.dialogTitle(item.fileName),
          imageAlt: alt,
          placeholder: labels.placeholder,
          loading: labels.placeholder,
          close: labels.close,
        }}
        loadImageSrc={async () => enlargedSrc ?? null}
        trigger={(
          <button
            aria-label={labels.enlarge(item.fileName)}
            className="w-full rounded-[var(--radius-control)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
            type="button"
          >
            {thumb}
          </button>
        )}
      />
      <MediaMeta item={item} labels={labels} />
      {onDownload != null ? <DownloadButton item={item} labels={labels} onDownload={onDownload} /> : null}
    </div>
  );
}

function GalleryDocument({ item, labels, onDownload }: { item: MediaGalleryItem; labels: MediaGalleryLabels; onDownload?: (item: MediaGalleryItem) => void }) {
  const metaText = labels.metaLine?.(item) ?? null;
  return (
    <div className="flex h-full flex-col gap-3 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] p-3">
      <div className="flex items-center gap-2 text-[var(--foreground-muted)]">
        <DocumentIcon />
        <div className="min-w-0">
          <span className="block truncate text-sm font-medium text-[var(--foreground)]" title={item.fileName}>{item.fileName}</span>
          {metaText != null && <span className="block truncate text-xs text-[var(--foreground-muted)]">{metaText}</span>}
        </div>
      </div>
      {onDownload != null
        ? <DownloadButton item={item} labels={labels} onDownload={onDownload} />
        : item.downloadRoute != null
          ? (
            <a
              aria-label={labels.download(item.fileName)}
              className="mt-auto inline-flex items-center justify-center gap-1 rounded-[var(--radius-control)] border border-[var(--border)] px-3 py-1.5 text-sm font-medium text-[var(--secondary)] hover:bg-[var(--background-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
              download
              href={item.downloadRoute}
            >
              <DownloadIcon />
              {labels.download(item.fileName)}
            </a>
          )
          : null}
    </div>
  );
}

/** The bucket driving the type badge and, in `layout="tabs"`, the filter tabs. */
function bucketOf(item: MediaGalleryItem): string {
  return item.displayType ?? item.typeInfo;
}

interface Group {
  typeInfo: string;
  label: string;
  items: MediaGalleryItem[];
}

function groupByType(items: MediaGalleryItem[], typeOrder: string[], groupLabel: (typeInfo: string) => string): Group[] {
  // The mediaserver's type column is case-insensitive and its contents prove it: `label` and
  // `LABEL` both occur. Grouping or ranking on the raw spelling would split one heading into
  // two adjacent sections, and drop an unlisted spelling to the end of the page.
  const byType = new Map<string, MediaGalleryItem[]>();
  for (const item of items) {
    const key = item.typeInfo.toUpperCase();
    const existing = byType.get(key);
    if (existing == null) byType.set(key, [item]);
    else existing.push(item);
  }
  const normalisedOrder = typeOrder.map((typeInfo) => typeInfo.toUpperCase());
  const rank = (typeInfo: string) => {
    const index = normalisedOrder.indexOf(typeInfo);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };
  return [...byType.keys()]
    .sort((left, right) => rank(left) - rank(right))
    .map((typeInfo) => ({ typeInfo, label: groupLabel(typeInfo), items: byType.get(typeInfo)! }));
}

function SelectionCheckbox({ item, labels, selection }: { item: MediaGalleryItem; labels: MediaGalleryLabels; selection: MediaGallerySelection }) {
  const selected = selection.selectedIds.has(item.mediaId);
  const label = labels.select?.(item.fileName, selected) ?? item.fileName;
  return (
    <button
      aria-label={label}
      aria-pressed={selected}
      className={cn(
        'inline-flex h-5 w-5 items-center justify-center rounded-[var(--radius-control)] border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]',
        selected ? 'border-[var(--secondary)] bg-[var(--secondary)] text-[var(--secondary-foreground,white)]' : 'border-[var(--border)] bg-[var(--background)]',
      )}
      onClick={() => selection.onToggle(item.mediaId)}
      type="button"
    >
      {selected
        ? <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24"><path d="m5 12 4 4L19 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" /></svg>
        : null}
    </button>
  );
}

/**
 * One grid tile: optional header row (selection checkbox + type badge) above the
 * image/document body. When `labels.typeBadge` is not supplied (h2-3's internal gallery),
 * the header row is entirely omitted and the tile renders exactly as before — only the
 * plain selection checkbox, unchanged.
 */
function GalleryTile({ item, labels, onDownload, selection }: { item: MediaGalleryItem; labels: MediaGalleryLabels; onDownload?: (item: MediaGalleryItem) => void; selection?: MediaGallerySelection }) {
  const body = item.kind === 'image'
    ? <GalleryImage item={item} labels={labels} onDownload={onDownload} />
    : <GalleryDocument item={item} labels={labels} onDownload={onDownload} />;

  if (labels.typeBadge == null) {
    return (
      <li className="flex flex-col gap-2">
        {selection != null ? <SelectionCheckbox item={item} labels={labels} selection={selection} /> : null}
        {body}
      </li>
    );
  }

  return (
    <li className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        {selection != null ? <SelectionCheckbox item={item} labels={labels} selection={selection} /> : <span aria-hidden="true" />}
        <Badge variant="outline">{labels.typeBadge(bucketOf(item))}</Badge>
      </div>
      {body}
    </li>
  );
}

interface Bucket {
  bucket: string;
  items: MediaGalleryItem[];
}

function bucketize(items: MediaGalleryItem[]): Bucket[] {
  const byBucket = new Map<string, MediaGalleryItem[]>();
  for (const item of items) {
    const bucket = bucketOf(item);
    const existing = byBucket.get(bucket);
    if (existing == null) byBucket.set(bucket, [item]);
    else existing.push(item);
  }
  return [...byBucket.entries()].map(([bucket, bucketItems]) => ({ bucket, items: bucketItems }));
}

export function MediaGallery({ className, items, labels, onDownload, selection, typeOrder = DEFAULT_TYPE_ORDER, layout = 'grouped', ...props }: MediaGalleryProps) {
  const groups = useMemo(() => groupByType(items, typeOrder, labels.groupLabel), [items, typeOrder, labels]);
  const buckets = useMemo(() => bucketize(items), [items]);
  const [activeBucket, setActiveBucket] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <div className={cn('rounded-[var(--radius-card)] border border-dashed border-[var(--border)] bg-[var(--background-muted)] p-6 text-center text-sm text-[var(--foreground-muted)]', className)} {...props}>
        {labels.emptyState}
      </div>
    );
  }

  if (layout === 'tabs') {
    const visibleItems = activeBucket == null ? items : items.filter((item) => bucketOf(item) === activeBucket);
    return (
      <div className={cn('flex flex-col gap-4', className)} {...props}>
        <div aria-label={labels.tabGroupLabel} className="flex flex-wrap gap-2" role="group">
          <button
            aria-pressed={activeBucket === null}
            className={cn(
              'rounded-full border px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]',
              activeBucket === null ? 'border-[var(--secondary)] bg-[var(--secondary)] text-[var(--secondary-foreground,white)]' : 'border-[var(--border)] bg-[var(--background)] text-[var(--foreground)] hover:bg-[var(--background-muted)]',
            )}
            onClick={() => setActiveBucket(null)}
            type="button"
          >
            {labels.tabLabel?.(null, items.length)}
          </button>
          {buckets.map(({ bucket, items: bucketItems }) => (
            <button
              aria-pressed={activeBucket === bucket}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]',
                activeBucket === bucket ? 'border-[var(--secondary)] bg-[var(--secondary)] text-[var(--secondary-foreground,white)]' : 'border-[var(--border)] bg-[var(--background)] text-[var(--foreground)] hover:bg-[var(--background-muted)]',
              )}
              key={bucket}
              onClick={() => setActiveBucket(bucket)}
              type="button"
            >
              {labels.tabLabel?.(bucket, bucketItems.length)}
            </button>
          ))}
        </div>
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {visibleItems.map((item) => (
            <GalleryTile item={item} key={item.mediaId} labels={labels} onDownload={onDownload} selection={selection} />
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      {groups.map((group) => (
        <section aria-label={group.label} className="flex flex-col gap-3" key={group.typeInfo}>
          <h3 className="text-sm font-semibold text-[var(--foreground)]">{group.label}</h3>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {group.items.map((item) => (
              <GalleryTile item={item} key={item.mediaId} labels={labels} onDownload={onDownload} selection={selection} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
